import { Headline2 } from 'pages/UserProfile/Header/styled';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { INVOICES_PER_PAGE } from 'services/billing';
import { getBillingInvoices } from 'store/actionCreators/subscriptions';
import {
  selectBillingInvoices,
  selectBillingInvoicesError,
  selectBillingInvoicesLoaded,
  selectIsBillingInvoicesLoading,
} from 'store/selectors/subscriptions';
import type {
  BillingInvoice,
  BillingInvoiceStatus,
} from 'types/billingInvoice';
import { formatSubscriptionDate } from 'utils/dates';
import { formatMoney } from 'utils/money';

import { Badge, CardSubtitle, UsageCard, UsageHeader } from '../styled';
import {
  AmountCell,
  DesktopTableWrap,
  DownloadLink,
  DownloadStack,
  DownloadUnavailable,
  EmptyIconCell,
  EmptyIconWrap,
  EmptyTableRow,
  InvoiceMeta,
  InvoicePrimary,
  InvoiceTable,
  ListViewport,
  MobileDownloads,
  MobileInvoiceCard,
  MobileInvoiceCardPlaceholder,
  MobileLabel,
  MobileList,
  MobileRow,
  PageNav,
  PageNavButton,
  PaginationFooter,
  PaginationSummary,
  SectionHeaderBlock,
  SkeletonBar,
  StatusBadgeOpen,
  TableScroll,
  Td,
  Th,
} from './styled';

const EmptyInvoicesIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 13h8v2H8v-2zm0 4h5v2H8v-2zm0-8h3v2H8V9z" />
  </svg>
);

const IconDownload = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
  </svg>
);

const IconChevronLeft = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
  </svg>
);

const statusLabelKey: Record<BillingInvoiceStatus, string> = {
  open: 'subscriptions.profile.invoiceHistory.statusOpen',
  paid: 'subscriptions.profile.invoiceHistory.statusPaid',
  void: 'subscriptions.profile.invoiceHistory.statusVoid',
};

const padToPageSize = (items: BillingInvoice[]): (BillingInvoice | null)[] => {
  const slots: (BillingInvoice | null)[] = [...items];
  while (slots.length < INVOICES_PER_PAGE) {
    slots.push(null);
  }
  return slots;
};

const InvoiceStatusBadge = ({ status }: { status: BillingInvoiceStatus }) => {
  const { t } = useTranslation();

  if (status === 'open') {
    return (
      <StatusBadgeOpen as="span">{t(statusLabelKey.open)}</StatusBadgeOpen>
    );
  }

  return (
    <Badge $tone={status === 'paid' ? 'teal' : 'slate'}>
      {t(statusLabelKey[status])}
    </Badge>
  );
};

const InvoiceHistory = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const invoices = useSelector(selectBillingInvoices);
  const isLoading = useSelector(selectIsBillingInvoicesLoading);
  const invoicesLoaded = useSelector(selectBillingInvoicesLoaded);
  const invoicesError = useSelector(selectBillingInvoicesError);
  const [page, setPage] = useState(1);

  const loadFailed = Boolean(invoicesError) && !isLoading;
  const showLoading =
    (isLoading && !invoicesLoaded) || (isLoading && invoices.length === 0);

  useEffect(() => {
    dispatch(getBillingInvoices.trigger());
  }, [dispatch]);

  useEffect(() => {
    setPage(1);
  }, [invoices.length]);

  const totalCount = invoices.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / INVOICES_PER_PAGE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageInvoices = useMemo(() => {
    const start = (page - 1) * INVOICES_PER_PAGE;
    return invoices.slice(start, start + INVOICES_PER_PAGE);
  }, [invoices, page]);

  const paddedPageSlots = useMemo(
    () => padToPageSize(pageInvoices),
    [pageInvoices],
  );

  const hasInvoices = totalCount > 0;

  const rangeFrom = totalCount === 0 ? 0 : (page - 1) * INVOICES_PER_PAGE + 1;
  const rangeTo = Math.min(page * INVOICES_PER_PAGE, totalCount);

  const showListFooter = hasInvoices && !showLoading && !loadFailed;

  const renderDownloads = (invoice: BillingInvoice, stacked = false) => {
    const invoiceHref = invoice.invoiceUrl?.trim() || null;
    const receiptHref = invoice.receiptUrl?.trim() || null;

    const invoiceControl = invoiceHref ? (
      <DownloadLink
        href={invoiceHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        <IconDownload />
        {t('subscriptions.profile.invoiceHistory.downloadInvoice')}
      </DownloadLink>
    ) : (
      <DownloadUnavailable>
        <IconDownload />
        {t('subscriptions.profile.invoiceHistory.downloadInvoice')}
      </DownloadUnavailable>
    );

    const receiptControl = receiptHref ? (
      <DownloadLink
        href={receiptHref}
        target="_blank"
        rel="noopener noreferrer"
      >
        <IconDownload />
        {t('subscriptions.profile.invoiceHistory.downloadReceipt')}
      </DownloadLink>
    ) : (
      <DownloadUnavailable>
        <IconDownload />
        {t('subscriptions.profile.invoiceHistory.downloadReceipt')}
      </DownloadUnavailable>
    );

    const links = (
      <>
        {invoiceControl}
        {receiptControl}
      </>
    );

    if (stacked) {
      return <DownloadStack>{links}</DownloadStack>;
    }

    return <MobileDownloads>{links}</MobileDownloads>;
  };

  const renderDesktopRow = (invoice: BillingInvoice) => (
    <tr key={invoice.id}>
      <Td $showDivider>
        <InvoicePrimary>
          {invoice.number ||
            t('subscriptions.profile.invoiceHistory.invoiceFallback', {
              id: invoice.id.slice(-8),
            })}
        </InvoicePrimary>
      </Td>
      <Td $showDivider>
        {formatSubscriptionDate(invoice.issuedAt, i18n.language)}
      </Td>
      <Td $showDivider>
        {invoice.dueAt
          ? formatSubscriptionDate(invoice.dueAt, i18n.language)
          : t('subscriptions.profile.invoiceHistory.noDueDate')}
      </Td>
      <Td $showDivider>
        <AmountCell>{formatMoney(invoice.currency, invoice.amount)}</AmountCell>
      </Td>
      <Td $showDivider>
        <InvoiceStatusBadge status={invoice.status} />
      </Td>
      <Td $alignRight $showDivider>
        {renderDownloads(invoice, true)}
      </Td>
    </tr>
  );

  const renderMobileCard = (invoice: BillingInvoice) => (
    <MobileInvoiceCard key={invoice.id}>
      <MobileRow>
        <div>
          <InvoicePrimary>
            {invoice.number ||
              t('subscriptions.profile.invoiceHistory.invoiceFallback', {
                id: invoice.id.slice(-8),
              })}
          </InvoicePrimary>
          <InvoiceMeta>
            {t('subscriptions.profile.invoiceHistory.issuedOn', {
              date: formatSubscriptionDate(invoice.issuedAt, i18n.language),
            })}
            {invoice.dueAt
              ? ` · ${t('subscriptions.profile.invoiceHistory.dueOn', {
                  date: formatSubscriptionDate(invoice.dueAt, i18n.language),
                })}`
              : null}
          </InvoiceMeta>
        </div>
        <InvoiceStatusBadge status={invoice.status} />
      </MobileRow>
      <MobileRow>
        <MobileLabel>
          {t('subscriptions.profile.invoiceHistory.colAmount')}
        </MobileLabel>
        <AmountCell>{formatMoney(invoice.currency, invoice.amount)}</AmountCell>
      </MobileRow>
      {renderDownloads(invoice)}
    </MobileInvoiceCard>
  );

  const tableHead = (
    <thead>
      <tr>
        <Th scope="col">
          {t('subscriptions.profile.invoiceHistory.colInvoice')}
        </Th>
        <Th scope="col">
          {t('subscriptions.profile.invoiceHistory.colIssued')}
        </Th>
        <Th scope="col">{t('subscriptions.profile.invoiceHistory.colDue')}</Th>
        <Th scope="col">
          {t('subscriptions.profile.invoiceHistory.colAmount')}
        </Th>
        <Th scope="col">
          {t('subscriptions.profile.invoiceHistory.colStatus')}
        </Th>
        <Th scope="col">
          {t('subscriptions.profile.invoiceHistory.colDownload')}
        </Th>
      </tr>
    </thead>
  );

  const renderSkeletonDesktopRow = (index: number) => (
    <tr key={`desktop-skeleton-${index}`} aria-hidden>
      <Td $showDivider>
        <SkeletonBar className="skeleton" $width="88px" />
      </Td>
      <Td $showDivider>
        <SkeletonBar className="skeleton" $width="72px" />
      </Td>
      <Td $showDivider>
        <SkeletonBar className="skeleton" $width="72px" />
      </Td>
      <Td $showDivider>
        <SkeletonBar className="skeleton" $width="56px" />
      </Td>
      <Td $showDivider>
        <SkeletonBar className="skeleton" $width="52px" $height="22px" />
      </Td>
      <Td $alignRight $showDivider>
        <DownloadStack>
          <SkeletonBar className="skeleton" $width="96px" $height="13px" />
          <SkeletonBar className="skeleton" $width="96px" $height="13px" />
        </DownloadStack>
      </Td>
    </tr>
  );

  const renderSkeletonMobileCard = (index: number) => (
    <MobileInvoiceCard key={`mobile-skeleton-${index}`} aria-hidden>
      <MobileRow>
        <div>
          <SkeletonBar className="skeleton" $width="120px" $height="14px" />
          <InvoiceMeta>
            <SkeletonBar className="skeleton" $width="160px" $height="12px" />
          </InvoiceMeta>
        </div>
        <SkeletonBar className="skeleton" $width="52px" $height="22px" />
      </MobileRow>
      <MobileRow>
        <MobileLabel>
          {t('subscriptions.profile.invoiceHistory.colAmount')}
        </MobileLabel>
        <SkeletonBar className="skeleton" $width="56px" />
      </MobileRow>
      <MobileDownloads>
        <SkeletonBar className="skeleton" $width="110px" $height="13px" />
        <SkeletonBar className="skeleton" $width="110px" $height="13px" />
      </MobileDownloads>
    </MobileInvoiceCard>
  );

  let listBody: React.ReactNode;

  if (showLoading) {
    listBody = (
      <>
        <DesktopTableWrap>
          <TableScroll>
            <InvoiceTable aria-busy="true">
              {tableHead}
              <tbody>
                {Array.from({ length: INVOICES_PER_PAGE }, (_, index) =>
                  renderSkeletonDesktopRow(index),
                )}
              </tbody>
            </InvoiceTable>
          </TableScroll>
        </DesktopTableWrap>

        <MobileList aria-busy="true">
          {Array.from({ length: INVOICES_PER_PAGE }, (_, index) =>
            renderSkeletonMobileCard(index),
          )}
        </MobileList>
      </>
    );
  } else if (loadFailed || !hasInvoices) {
    listBody = (
      <>
        <DesktopTableWrap>
          <TableScroll>
            <InvoiceTable>
              {tableHead}
              <tbody>
                <tr>
                  <EmptyIconCell
                    colSpan={6}
                    role={loadFailed ? 'alert' : undefined}
                  >
                    <EmptyIconWrap>
                      <EmptyInvoicesIcon />
                    </EmptyIconWrap>
                  </EmptyIconCell>
                </tr>
              </tbody>
            </InvoiceTable>
          </TableScroll>
        </DesktopTableWrap>

        <MobileList>
          <EmptyIconWrap role={loadFailed ? 'alert' : undefined}>
            <EmptyInvoicesIcon />
          </EmptyIconWrap>
        </MobileList>
      </>
    );
  } else {
    listBody = (
      <>
        <DesktopTableWrap>
          <TableScroll>
            <InvoiceTable>
              {tableHead}
              <tbody>
                {paddedPageSlots.map((invoice, index) =>
                  invoice ? (
                    renderDesktopRow(invoice)
                  ) : (
                    <EmptyTableRow key={`desktop-pad-${index}`} aria-hidden>
                      <td colSpan={6}>&nbsp;</td>
                    </EmptyTableRow>
                  ),
                )}
              </tbody>
            </InvoiceTable>
          </TableScroll>
        </DesktopTableWrap>

        <MobileList>
          {paddedPageSlots.map((invoice, index) =>
            invoice ? (
              renderMobileCard(invoice)
            ) : (
              <MobileInvoiceCardPlaceholder
                key={`mobile-pad-${index}`}
                aria-hidden
              />
            ),
          )}
        </MobileList>
      </>
    );
  }

  return (
    <UsageCard aria-labelledby="invoice-history-heading">
      <UsageHeader>
        <SectionHeaderBlock>
          <Headline2 id="invoice-history-heading" style={{ margin: 0 }}>
            {t('subscriptions.profile.invoiceHistory.title')}
          </Headline2>
          <CardSubtitle style={{ margin: 0 }}>
            {t('subscriptions.profile.invoiceHistory.subtitle')}
          </CardSubtitle>
        </SectionHeaderBlock>
      </UsageHeader>

      <ListViewport>{listBody}</ListViewport>

      {showListFooter ? (
        <PaginationFooter>
          <PaginationSummary>
            {t('subscriptions.profile.invoiceHistory.showingRange', {
              from: rangeFrom,
              to: rangeTo,
              total: totalCount,
            })}
          </PaginationSummary>
          <PageNav
            aria-label={t(
              'subscriptions.profile.invoiceHistory.paginationLabel',
            )}
          >
            <PageNavButton
              type="button"
              disabled={page <= 1}
              aria-label={t(
                'subscriptions.profile.invoiceHistory.previousPage',
              )}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <IconChevronLeft />
            </PageNavButton>
            <PageNavButton
              type="button"
              disabled={page >= totalPages}
              aria-label={t('subscriptions.profile.invoiceHistory.nextPage')}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <IconChevronRight />
            </PageNavButton>
          </PageNav>
        </PaginationFooter>
      ) : null}
    </UsageCard>
  );
};

export default InvoiceHistory;
